import { ActiveTouchAction, type ActiveTouchActionProps } from '@shared-ui/components/ui/active-touch-action'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { GText } from '@shared-ui/components/ui/text'
import { useHaptic } from '@shared-ui/platform/hooks/use-haptic'
import { createHitSlop } from '@shared-ui/platform/view'
import { useAppTheme } from '@shared-ui/theme'
import type { PropsWithChildren } from 'react'
import {
  type GestureResponderEvent,
  type StyleProp,
  type TextStyle,
  View,
  type ViewProps,
  type ViewStyle,
} from 'react-native'
import { useCreateStyles } from './styles'

type CheckboxAppearance = {
  size?: 'medium' | 'large'
  isDanger?: boolean
  textStyle?: StyleProp<TextStyle>
  checkboxStyle?: StyleProp<ViewStyle>
}

type CheckboxProps = PropsWithChildren<{
  isActive: boolean
  isCard?: boolean
  appearance?: CheckboxAppearance
  onPress?: ActiveTouchActionProps['onPress']
  style?: ViewProps['style']
  testID?: string
}>

export function Checkbox({ isActive, isCard, appearance, children, onPress, style, testID }: CheckboxProps) {
  const { size = 'medium', isDanger, textStyle, checkboxStyle } = appearance ?? {}
  const theme = useAppTheme()
  const styles = useCreateStyles()
  const haptic = useHaptic()

  const handlePress = (e: GestureResponderEvent) => {
    if (onPress) {
      haptic('selection')
      onPress(e)
    }
  }

  const stylesWrapper: Array<StyleProp<ViewStyle> | undefined> = [styles.wrapper]
  const stylesCheckbox: Array<StyleProp<ViewStyle> | undefined> = [styles.checkbox]
  let iconSize = 16

  if (isCard) {
    stylesWrapper.push(styles.card)
  }

  if (style) {
    stylesWrapper.push(style)
  }

  if (size === 'large') {
    stylesCheckbox.push(styles.checkboxLarge)
    iconSize = 20
  }

  if (size === 'medium') {
    stylesCheckbox.push(styles.checkboxMedium)
  }

  if (isActive) {
    stylesCheckbox.push(styles.checkboxActive)
  }

  if (isActive && isDanger) {
    stylesCheckbox.push(styles.checkboxActiveDanger)
  }

  if (checkboxStyle) {
    stylesCheckbox.push(checkboxStyle)
  }

  const content = (
    <>
      <View style={stylesCheckbox}>
        {isActive && <AppIcon icon="check" size={iconSize} color={theme.COLOR_ICON_FIXED} />}
      </View>
      <GText size={14} fw={400} style={[styles.text, textStyle]}>
        {children}
      </GText>
    </>
  )

  if (!onPress) {
    return (
      <View style={[stylesWrapper, { pointerEvents: 'none' }]} testID={testID}>
        {content}
      </View>
    )
  }

  return (
    <ActiveTouchAction style={stylesWrapper} onPress={handlePress} hitSlop={createHitSlop(15)} testID={testID}>
      {content}
    </ActiveTouchAction>
  )
}
